import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    CalendarDays,
    Clock,
    MapPin,
    User as UserIcon,
    BookOpen,
    Target,
    ClipboardCheck,
    FileText,
    Download,
    Lock,
    CheckCircle2,
    Receipt,
    Trophy,
    BadgeCheck,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import SessionQualiopiBlock from "@/components/stagiaire/SessionQualiopiBlock";

L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DOC_ICONS = {
    programme: FileText,
    convention: BookOpen,
    emargement: ClipboardCheck,
    facture: Receipt,
    attestation: CheckCircle2,
};

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

function downloadDocument(sessionId, type) {
    const token = (() => {
        try { return localStorage.getItem("cursus_physio_token"); } catch { return null; }
    })();
    if (!token) {
        toast.error("Non authentifié");
        return;
    }
    fetch(`${API}/stagiaire/sessions/${sessionId}/documents/${type}.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
    })
        .then(async (r) => {
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || "Téléchargement impossible");
            }
            return r.blob();
        })
        .then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${type}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`${type} téléchargé`);
        })
        .catch((err) => toast.error(err.message || "Erreur"));
}

export default function StagiaireSessionDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/stagiaire/sessions/${id}`)
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <div className="p-10 text-brand-smoke">Chargement…</div>;
    if (!data) return <div className="p-10">Session introuvable.</div>;

    const { session, enrollment, documents } = data;
    const formation = session.formation || {};
    const cursus = session.cursus || {};
    const formateur = session.formateur;
    const cursusFormationsCount = 3; // standard
    // Derive Y/3 from formation order
    const formationY = formation.order || 1;

    const refresh = () => {
        api.get(`/stagiaire/sessions/${id}`).then((r) => setData(r.data)).catch(() => {});
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-stagiaire-session-detail">
            <Link to="/stagiaire/sessions" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-smoke hover:text-brand-orange">
                <ArrowLeft className="h-3.5 w-3.5" /> Retour à mes sessions
            </Link>

            {/* Hero */}
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative mt-6 overflow-hidden rounded-3xl shadow-premium-lg"
            >
                <img src={cursus.image_url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/80 to-brand-ink/30" />
                <div className="relative p-8 text-white md:p-12">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/[0.06] px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] backdrop-blur">
                        {cursus.name?.replace("Cursus ", "")} · Formation {formationY}/{cursusFormationsCount}
                    </div>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-normal leading-[1] tracking-tightest md:text-6xl">
                        {formation.name}
                    </h1>
                    <p className="mt-4 max-w-2xl text-base text-white/75">
                        {formation.short_description}
                    </p>
                </div>
            </motion.div>

            {/* Qualiopi : evaluations + emargement */}
            <div className="mt-8">
                <SessionQualiopiBlock
                    sessionId={session.id}
                    enrollmentId={enrollment.id}
                    sessionDay={data.emargement?.is_session_day}
                    sessionStartDate={session.start_date}
                    emargement={data.emargement}
                    onUpdate={refresh}
                />
            </div>

            <div className="mt-10 grid gap-8 lg:grid-cols-12">
                {/* Main */}
                <article className="space-y-10 lg:col-span-8">
                    {/* Infos pratiques */}
                    <section className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium" data-testid="session-infos-pratiques">
                        <h2 className="font-display text-2xl">Informations pratiques</h2>
                        <dl className="mt-6 grid gap-x-8 gap-y-5 sm:grid-cols-2">
                            <div className="flex items-start gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                    <CalendarDays className="h-4 w-4" />
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-wider text-brand-smoke">Date</dt>
                                    <dd className="mt-1 font-medium text-brand-ink">{formatDate(session.start_date)}</dd>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                    <Clock className="h-4 w-4" />
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-wider text-brand-smoke">Horaires</dt>
                                    <dd className="mt-1 font-medium text-brand-ink">
                                        {session.start_time} – {session.end_time} ({formation.duration_hours || 7}h)
                                    </dd>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 sm:col-span-2">
                                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                    <MapPin className="h-4 w-4" />
                                </div>
                                <div className="flex-1">
                                    <dt className="text-xs uppercase tracking-wider text-brand-smoke">Lieu</dt>
                                    <dd className="mt-1 text-brand-ink">
                                        <div className="font-semibold">{session.venue_room_name}</div>
                                        <div className="text-sm">{session.venue_name}</div>
                                        <div className="text-sm text-brand-smoke">{session.venue_address}, {session.venue_postcode} {session.city}</div>
                                    </dd>
                                </div>
                            </div>
                        </dl>

                        {/* Mini-map */}
                        <div className="mt-6 overflow-hidden rounded-2xl border border-brand-line" data-testid="session-mini-map">
                            <div className="h-56 w-full">
                                <MapContainer
                                    center={[
                                        session.city === "Paris" ? 48.857 :
                                        session.city === "Lyon" ? 45.764 :
                                        session.city === "Marseille" ? 43.296 :
                                        session.city === "Bordeaux" ? 44.838 : 46.6,
                                        session.city === "Paris" ? 2.353 :
                                        session.city === "Lyon" ? 4.835 :
                                        session.city === "Marseille" ? 5.370 :
                                        session.city === "Bordeaux" ? -0.578 : 1.9,
                                    ]}
                                    zoom={12}
                                    scrollWheelZoom={false}
                                    style={{ height: "100%", width: "100%" }}
                                >
                                    <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                    <Marker position={[
                                        session.city === "Paris" ? 48.857 :
                                        session.city === "Lyon" ? 45.764 :
                                        session.city === "Marseille" ? 43.296 :
                                        session.city === "Bordeaux" ? 44.838 : 46.6,
                                        session.city === "Paris" ? 2.353 :
                                        session.city === "Lyon" ? 4.835 :
                                        session.city === "Marseille" ? 5.370 :
                                        session.city === "Bordeaux" ? -0.578 : 1.9,
                                    ]}>
                                        <Popup>{session.venue_name} — {session.venue_address}</Popup>
                                    </Marker>
                                </MapContainer>
                            </div>
                        </div>
                    </section>

                    {/* Programme */}
                    {formation.program_modules?.length > 0 && (
                        <section className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                            <h2 className="font-display text-2xl">Programme de la journée</h2>
                            <ol className="mt-6 space-y-4">
                                {formation.program_modules.map((m, i) => (
                                    <li key={i} className="rounded-xl border border-brand-line bg-[#FAFAFA] p-5">
                                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                                            <div className="font-display text-lg text-brand-ink">Module {String(i + 1).padStart(2, "0")} · {m.title}</div>
                                            <span className="rounded-full bg-brand-orange/10 px-2.5 py-0.5 text-xs font-semibold text-brand-orange">{m.duration}</span>
                                        </div>
                                        <p className="mt-2 text-sm leading-relaxed text-brand-ink/75">{m.content}</p>
                                    </li>
                                ))}
                            </ol>
                        </section>
                    )}

                    {/* Objectifs */}
                    {formation.objectives?.length > 0 && (
                        <section className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                            <div className="flex items-center gap-3">
                                <Target className="h-5 w-5 text-brand-orange" />
                                <h2 className="font-display text-2xl">Objectifs pédagogiques</h2>
                            </div>
                            <ul className="mt-5 space-y-2 text-sm text-brand-ink/85">
                                {formation.objectives.map((o, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" /> {o}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* Méthodes d'évaluation */}
                    {formation.evaluation_methods?.length > 0 && (
                        <section className="rounded-2xl border border-brand-line bg-white p-8 shadow-premium">
                            <div className="flex items-center gap-3">
                                <ClipboardCheck className="h-5 w-5 text-brand-orange" />
                                <h2 className="font-display text-2xl">Méthodes d'évaluation</h2>
                            </div>
                            <ul className="mt-5 space-y-2 text-sm text-brand-ink/85">
                                {formation.evaluation_methods.map((o, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" /> {o}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </article>

                {/* Sidebar */}
                <aside className="lg:col-span-4">
                    <div className="space-y-5 lg:sticky lg:top-6">
                        {/* Formateur */}
                        {formateur && (
                            <div className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium" data-testid="session-formateur-card">
                                <div className="border-b border-brand-line px-6 py-4 text-xs font-semibold uppercase tracking-wider text-brand-orange">
                                    Votre formateur
                                </div>
                                {formateur.is_public_profile && formateur.photo_url ? (
                                    <div className="aspect-[4/3] w-full overflow-hidden bg-brand-sand">
                                        <img src={formateur.photo_url} alt={formateur.name} loading="lazy" className="h-full w-full object-cover" />
                                    </div>
                                ) : null}
                                <div className="p-6">
                                    <div className="flex items-center gap-4">
                                        {!formateur.is_public_profile && (
                                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange/10 font-display text-2xl text-brand-orange">
                                                {(formateur.first_name || "?")[0]}
                                            </div>
                                        )}
                                        <div>
                                            <div className="font-display text-xl text-brand-ink">{formateur.name}</div>
                                            {formateur.professional_title ? (
                                                <div className="text-xs italic text-brand-smoke">{formateur.professional_title}</div>
                                            ) : (
                                                <div className="text-xs text-brand-smoke">{cursus.name?.replace("Cursus ", "")}</div>
                                            )}
                                        </div>
                                    </div>
                                    <p className="mt-4 text-sm leading-relaxed text-brand-ink/75">
                                        {formateur.short_bio || session.trainer_bio}
                                    </p>
                                    {(formateur.specialties || []).length > 0 && (
                                        <div className="mt-4 flex flex-wrap gap-1.5">
                                            {formateur.specialties.slice(0, 3).map((s) => (
                                                <span key={s} className="rounded-full bg-brand-orange/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-orange">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {(formateur.qualifications || []).length > 0 && (
                                        <ul className="mt-5 space-y-1.5 text-xs text-brand-ink/70">
                                            {formateur.qualifications.slice(0, 3).map((q, i) => (
                                                <li key={i} className="flex items-start gap-2">
                                                    <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-brand-orange" />
                                                    <span>
                                                        <strong>{q.name}</strong>
                                                        {q.year ? <span className="ml-1 opacity-60">— {q.year}</span> : null}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {((formateur.sports_associations || []).length > 0 || (formateur.professional_associations || []).length > 0) && (
                                        <div className="mt-5 space-y-3" data-testid="session-formateur-associations">
                                            {(formateur.sports_associations || []).length > 0 && (
                                                <div>
                                                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-orange">
                                                        <Trophy className="h-3 w-3" /> Associations sportives
                                                    </div>
                                                    <ul className="mt-1.5 space-y-1 text-xs text-brand-ink/70">
                                                        {formateur.sports_associations.map((a) => (
                                                            <li key={a} className="flex items-start gap-2">
                                                                <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-brand-ink/40" />
                                                                <span>{a}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {(formateur.professional_associations || []).length > 0 && (
                                                <div>
                                                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-orange">
                                                        <BadgeCheck className="h-3 w-3" /> Associations professionnelles
                                                    </div>
                                                    <ul className="mt-1.5 space-y-1 text-xs text-brand-ink/70">
                                                        {formateur.professional_associations.map((a) => (
                                                            <li key={a} className="flex items-start gap-2">
                                                                <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-brand-ink/40" />
                                                                <span>{a}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Documents */}
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="session-documents-card">
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
                                Mes documents
                            </div>
                            <ul className="mt-4 space-y-2.5">
                                {documents.map((doc) => {
                                    const Icon = DOC_ICONS[doc.type] || FileText;
                                    return (
                                        <li
                                            key={doc.type}
                                            className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 ${doc.available ? "border-brand-line bg-white" : "border-dashed border-brand-line bg-[#FAFAFA] opacity-60"}`}
                                            data-testid={`document-row-${doc.type}`}
                                        >
                                            <div className="flex items-center gap-3 text-sm">
                                                <Icon className="h-4 w-4 text-brand-orange" />
                                                <span className="font-medium text-brand-ink">{doc.name}</span>
                                            </div>
                                            {doc.available ? (
                                                <button
                                                    type="button"
                                                    onClick={() => downloadDocument(session.id, doc.type)}
                                                    data-testid={`document-download-${doc.type}`}
                                                    className="inline-flex items-center gap-1 rounded-full bg-brand-ink px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-brand-coal"
                                                >
                                                    <Download className="h-3 w-3" /> Télécharger
                                                </button>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] text-brand-smoke" title={doc.available_when}>
                                                    <Lock className="h-3 w-3" /> {doc.available_when}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        {/* Récap */}
                        <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="session-summary-card">
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
                                Récapitulatif
                            </div>
                            <dl className="mt-4 space-y-3 text-sm">
                                <div className="flex items-center justify-between border-b border-brand-line pb-3">
                                    <dt className="text-brand-smoke">Tarif</dt>
                                    <dd className="font-display text-2xl text-brand-orange">{enrollment.payment_amount_eur} €</dd>
                                </div>
                                <div className="flex items-center justify-between border-b border-brand-line pb-3">
                                    <dt className="text-brand-smoke">Statut paiement</dt>
                                    <dd className="font-medium capitalize text-brand-ink">
                                        {enrollment.payment_status === "paid" ? "Payé" :
                                         enrollment.payment_status === "manual" ? "Payé (manuel)" :
                                         enrollment.payment_status === "pending" ? "En attente" :
                                         "Remboursé"}
                                    </dd>
                                </div>
                                <div className="flex items-center justify-between">
                                    <dt className="text-brand-smoke">Inscrit le</dt>
                                    <dd className="font-medium text-brand-ink">
                                        {new Date(enrollment.enrolled_at).toLocaleDateString("fr-FR")}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
